import React, {FC} from "react";
import {useTranslation} from "react-i18next";
import {Alert, App, Button, Input, Space} from "antd";
import {CopyOutlined, EyeOutlined} from "@ant-design/icons";
import {useParams} from "react-router-dom";
import {
    EventRegistrationFormTemplateStatus,
    useActivateEventRegistrationFormTemplateMutation,
    useCreateBasicEventRegistrationFormTemplateMutation,
    useEventRegistrationFormTemplatesForSetupQuery,
    useEventsListQuery
} from "../../GraphQL/generated/graphql";
import {
    buildHostedPublicRegistrationIframeSnippet,
    buildPublicRegistrationIframeId,
    buildHostedPublicRegistrationUrl,
    withPublicRegistrationEmbedMode
} from "./publicEventFormShare";
import {buildUserFacingErrorLabels, getUserFacingErrorMessage} from "../../userFacingErrorUtils";
import {PageHeader, Panel} from "../../Components/ui";

export const PublicEventFormsPage: FC = () => {
    const {message} = App.useApp();
    const {t} = useTranslation();
    const {eventId: routeEventId} = useParams();

    const eventNumericId = routeEventId ? Number(routeEventId) : undefined;
    const selectedEventQuery = useEventsListQuery({
        variables: eventNumericId
            ? {
                take: 1,
                where: {
                    id: {eq: eventNumericId}
                }
            }
            : undefined,
        fetchPolicy: "cache-and-network",
        skip: !eventNumericId
    });
    const formTemplatesQuery = useEventRegistrationFormTemplatesForSetupQuery({
        variables: {
            eventId: eventNumericId ?? 0
        },
        fetchPolicy: "cache-and-network",
        skip: !eventNumericId
    });
    const [createBasicTemplate, createBasicTemplateState] = useCreateBasicEventRegistrationFormTemplateMutation();
    const [activateTemplate, activateTemplateState] = useActivateEventRegistrationFormTemplateMutation();

    const selectedEvent = (selectedEventQuery.data?.events?.items ?? [])[0];
    const formTemplates = formTemplatesQuery.data?.eventRegistrationFormArbitraryFieldsTemplates?.items ?? [];
    const activeTemplate = formTemplates.find(template =>
        template.status === EventRegistrationFormTemplateStatus.Active
    );
    const latestDraftTemplate = formTemplates.find(template =>
        template.status === EventRegistrationFormTemplateStatus.Draft
    );
    const hasRegistrationBasics = !!(
        selectedEvent?.id &&
        selectedEvent.name?.trim() &&
        selectedEvent.startDate &&
        selectedEvent.endDate
    );
    const isTemplateStatusLoading = formTemplatesQuery.loading && !formTemplatesQuery.data;
    const isRegistrationReady = hasRegistrationBasics && !!activeTemplate;
    const isActivatingTemplate = createBasicTemplateState.loading || activateTemplateState.loading;
    const hostedRegistrationUrl = selectedEvent?.id
        ? buildHostedPublicRegistrationUrl(window.location.origin, selectedEvent.id)
        : "";
    const previewRegistrationUrl = hostedRegistrationUrl
        ? withPublicRegistrationEmbedMode(hostedRegistrationUrl)
        : "";
    const iframeSnippet = hostedRegistrationUrl
        ? buildHostedPublicRegistrationIframeSnippet(
            hostedRegistrationUrl,
            buildPublicRegistrationIframeId(selectedEvent.id))
        : "";

    const copyToClipboard = async (value: string) => {
        if (!value) {
            return;
        }

        if (!navigator.clipboard?.writeText) {
            message.warning(t("clipboard-unavailable", {defaultValue: "Clipboard is unavailable"}));
            return;
        }

        try {
            await navigator.clipboard.writeText(value);
            message.success(t("cta.copied", {defaultValue: "Copied"}));
        } catch {
            message.warning(t("clipboard-unavailable", {defaultValue: "Clipboard is unavailable"}));
        }
    };

    const activateBasicRegistrationForm = async () => {
        if (!eventNumericId || !hasRegistrationBasics || isActivatingTemplate) {
            return;
        }

        const errorLabels = buildUserFacingErrorLabels(
            t,
            t("public-event-forms.activation-failed", {
                defaultValue: "The registration form could not be activated."
            })
        );

        try {
            let templateId = latestDraftTemplate?.id;

            if (!templateId) {
                const createResult = await createBasicTemplate({
                    variables: {
                        input: {
                            eventId: eventNumericId,
                            status: EventRegistrationFormTemplateStatus.Draft
                        }
                    }
                });
                templateId = createResult.data?.createEventRegistrationFormArbitraryFieldsTemplate?.[0]?.id;
            }

            if (!templateId) {
                throw new Error(errorLabels.fallback);
            }

            await activateTemplate({
                variables: {
                    input: {
                        id: templateId
                    }
                }
            });
            await formTemplatesQuery.refetch();
            message.success(t("public-event-forms.activation-success", {
                defaultValue: "The registration form is active."
            }));
        } catch (error) {
            await formTemplatesQuery.refetch().catch(() => undefined);
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        }
    };

    return (
        <div className="ati-stack">
            <PageHeader level={2} title={t("setup-sequence.registration-title", {defaultValue: "Registration form"})}/>

            {selectedEvent ? (
                <>
                    <Alert
                        type={isRegistrationReady ? "success" : isTemplateStatusLoading ? "info" : "warning"}
                        showIcon
                        message={isTemplateStatusLoading
                            ? t("public-event-forms.readiness-checking", {defaultValue: "Checking"})
                            : isRegistrationReady
                                ? t("setup-flow.registration-ready", {defaultValue: "Ready"})
                                : !activeTemplate
                                    ? t("public-event-forms.readiness-inactive", {defaultValue: "Inactive"})
                                    : t("setup-flow.registration-missing", {defaultValue: "Review"})}
                        description={isTemplateStatusLoading
                            ? t("public-event-forms.readiness-checking-description", {
                                defaultValue: "Checking whether this event has an active public registration form."
                            })
                            : formTemplatesQuery.error
                                ? t("public-event-forms.readiness-check-failed", {
                                    defaultValue: "The registration form status could not be checked. Retry before sharing the link."
                                })
                                : !activeTemplate
                                    ? t("public-event-forms.readiness-activation-needed", {
                                        defaultValue: "Create and activate the basic form before sharing the hosted link."
                                    })
                                    : hasRegistrationBasics
                                        ? t("public-event-forms.readiness-share-ready", {
                                            defaultValue: "The hosted registration link is ready. Changes to setup appear there immediately."
                                        })
                                        : t("public-event-forms.readiness-basics-needed", {
                                            defaultValue: "Save event name, dates, and public copy in Settings before sharing the hosted link."
                                        })}
                        action={formTemplatesQuery.error ? (
                            <Button onClick={() => formTemplatesQuery.refetch()}>
                                {t("retry", {defaultValue: "Retry"})}
                            </Button>
                        ) : undefined}
                    />

                    {!isTemplateStatusLoading && !formTemplatesQuery.error && !activeTemplate ? (
                        <Alert
                            type="warning"
                            message={hasRegistrationBasics
                                ? t("public-event-forms.activation-description", {
                                    defaultValue: "Activate a basic form to start accepting public registrations."
                                })
                                : t("public-event-forms.activation-settings-required", {
                                    defaultValue: "Complete the event name, dates, and public copy before activating registrations."
                                })}
                            action={(
                                <Button
                                    type="primary"
                                    disabled={!hasRegistrationBasics}
                                    loading={isActivatingTemplate}
                                    onClick={activateBasicRegistrationForm}
                                >
                                    {t("public-event-forms.activate-basic", {defaultValue: "Activate basic form"})}
                                </Button>
                            )}
                        />
                    ) : null}

                    <Panel
                        title={t("public-registration-share", {defaultValue: "Public registration sharing"})}
                        description={t("public-registration-share-description", {
                            defaultValue: "Use the hosted URL for a plain WordPress link, or copy the iframe snippet for an allowlisted embed."
                        })}
                    >
                        <div className="ati-stack">
                            <Space.Compact block>
                                <Input
                                    readOnly
                                    value={hostedRegistrationUrl}
                                    aria-label={t("public-registration-hosted-url", {defaultValue: "Hosted registration URL"})}
                                />
                                <Button
                                    icon={<EyeOutlined/>}
                                    href={isRegistrationReady ? previewRegistrationUrl : undefined}
                                    target="_blank"
                                    rel="noreferrer"
                                    disabled={!isRegistrationReady}
                                >
                                    {t("preview", {defaultValue: "Preview"})}
                                </Button>
                                <Button
                                    icon={<CopyOutlined/>}
                                    onClick={() => copyToClipboard(hostedRegistrationUrl)}
                                    disabled={!isRegistrationReady}
                                >
                                    {t("cta.copy-public-link", {defaultValue: "Copy public link"})}
                                </Button>
                            </Space.Compact>

                            <Input.TextArea
                                readOnly
                                value={iframeSnippet}
                                rows={8}
                                aria-label={t("public-registration-iframe-snippet", {defaultValue: "Iframe embed snippet"})}
                            />

                            <div>
                                <Button
                                    icon={<CopyOutlined/>}
                                    onClick={() => copyToClipboard(iframeSnippet)}
                                    disabled={!isRegistrationReady}
                                >
                                    {t("public-registration-copy-iframe", {defaultValue: "Copy iframe"})}
                                </Button>
                            </div>
                        </div>
                    </Panel>
                </>
            ) : null}
        </div>
    );
};

export default PublicEventFormsPage;
